import { useEffect, type ReactNode } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { iniciarSincronizador } from './lib/cola-offline';
import { iniciarRastro } from './lib/rastro';
import { ProveedorApp, useApp } from './lib/contexto';
import type { PerfilPlataforma } from './lib/api-panel';
import { Encabezado } from './ui/navegacion/Encabezado';
import type { IdentidadPlataforma } from './ui/navegacion/MenuCuenta';
import type { GrupoMenu } from './ui/navegacion/navegacion';
import { CartaCodigos } from './paginas/catalogo/CartaCodigos';
import { Configuracion } from './paginas/Configuracion';
import { Catalogo } from './paginas/catalogo/Catalogo';
import { Comprobantes } from './paginas/Comprobantes';
import { CrearNegocio } from './paginas/CrearNegocio';
import { Cuadre } from './paginas/Cuadre';
import { DetalleComprobante } from './paginas/DetalleComprobante';
import { Hoy } from './paginas/Hoy';
import { Ingreso } from './paginas/Ingreso';
import { Mostrador } from './paginas/mostrador/Mostrador';
import { Perfil } from './paginas/Perfil';
import { Exportes } from './paginas/Exportes';
import { Gastos } from './paginas/gastos/Gastos';
import { Invitacion } from './paginas/Invitacion';
import { Panel } from './paginas/panel/Panel';
import { Portada } from './paginas/Portada';
import { Preferencias } from './paginas/Preferencias';
import { Recetas } from './paginas/Recetas';
import { Reportes } from './paginas/Reportes';
import { Selector } from './paginas/Selector';
import { Terceros } from './paginas/Terceros';
import { PoliticaDatos } from './paginas/legal/PoliticaDatos';
import { TerminosServicio } from './paginas/legal/TerminosServicio';
import { destinoSeguro } from './lib/destino';
import { seguirAlSistema } from './lib/tema';
import { Boton, Esqueleto, MensajeError, Tarjeta } from './ui/base';
import { AvisoCola } from './ui/AvisoCola';

/** Deja pasar solo si el rol alcanza; si no, devuelve a la raíz (que ya enruta por rol). */
function Permitir({ cuando, children }: { cuando: boolean; children: ReactNode }) {
  return cuando ? <>{children}</> : <Navigate to="/" replace />;
}

/**
 * A dónde va quien tiene sesión y ningún negocio activo: al panel si lo opera, a crear su negocio si
 * no tiene ninguno, y al selector si tiene varios.
 */
function SinNegocio({ superadmin, sinNegocios }: { superadmin: boolean | null; sinNegocios: boolean }) {
  if (sinNegocios) return <Navigate to={superadmin ? '/panel' : '/crear-negocio'} replace />;
  return <Selector />;
}

/** Guarda el destino de un deep-link privado para retomarlo tras el login. */
function GuardarDestino() {
  const ubicacion = useLocation();
  useEffect(() => {
    // El destino viene de la URL que pidió el visitante: se guarda solo si es una ruta interna.
    const seguro = destinoSeguro(ubicacion.pathname + ubicacion.search);
    if (seguro) sessionStorage.setItem('tanteo-volver', seguro);
  }, [ubicacion]);
  return <Navigate to="/ingresar" replace />;
}

/** Tras autenticarse, retoma el deep-link guardado (una sola vez). */
function RetomarDestino() {
  const navegar = useNavigate();
  useEffect(() => {
    const destino = destinoSeguro(sessionStorage.getItem('tanteo-volver'));
    sessionStorage.removeItem('tanteo-volver');
    if (destino) navegar(destino, { replace: true });
  }, [navegar]);
  return null;
}

function Enrutador() {
  const {
    cargando,
    sesion,
    negocio,
    negociosPropios,
    perfilPanel,
    esSuperadmin,
    fallóCarga,
    fallóPerfilPanel,
    reintentarPerfilPanel,
    salir,
  } = useApp();
  const ubicacion = useLocation();

  // Los documentos legales son públicos y se ven igual con sesión o sin ella: no esperan la carga.
  if (ubicacion.pathname.startsWith('/legal/')) {
    return (
      <Routes>
        <Route path="/legal/terminos-servicio" element={<TerminosServicio />} />
        <Route path="/legal/politica-datos" element={<PoliticaDatos />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    );
  }

  if (cargando) {
    return (
      <div className="mx-auto max-w-3xl p-6">
        <Esqueleto lineas={5} />
      </div>
    );
  }
  if (!sesion) {
    return (
      <Routes>
        <Route path="/" element={<Portada />} />
        <Route path="/ingresar" element={<Ingreso modo="entrar" />} />
        {/* Ruta anterior conservada para enlaces guardados */}
        <Route path="/login" element={<Navigate to="/ingresar" replace />} />
        <Route path="/crear-cuenta" element={<Ingreso modo="crear" />} />
        {/* La invitación se resuelve sin sesión: la página pide el código ella misma. */}
        <Route path="/invitacion" element={<Invitacion />} />
        <Route path="*" element={<GuardarDestino />} />
      </Routes>
    );
  }
  /*
    Si no se pudo leer el perfil de plataforma, esta pantalla no decide nada: dar por hecho que
    alguien no opera el panel lo manda al camino de quien no tiene negocio.
  */
  if (fallóPerfilPanel) {
    return (
      <main className="flex min-h-full items-center justify-center p-4">
        <Tarjeta className="w-full max-w-3xl">
          <h1 className="mb-2 text-titulo font-extrabold">No pudimos leer tu perfil</h1>
          <MensajeError>Revisa la conexión e inténtalo de nuevo.</MensajeError>
          <div className="mt-4 flex items-center justify-between gap-2">
            <Boton onClick={() => void reintentarPerfilPanel()} data-e2e="reintentar-perfil">
              Reintentar
            </Boton>
            <button onClick={() => void salir()} className="text-info text-contenido underline">
              Salir
            </button>
          </div>
        </Tarjeta>
      </main>
    );
  }
  // Sin resolver todavía si opera el panel: sin esta espera, el selector alcanza a parpadear
  // antes de que la redirección lo saque de ahí.
  if (esSuperadmin === null) {
    return (
      <div className="mx-auto max-w-3xl p-6">
        <Esqueleto lineas={5} />
      </div>
    );
  }

  // Sin negocio activo, el encabezado sigue siendo el mismo: cambia lo que lleva dentro. Quien
  // opera el panel ve sus secciones; quien no, no ve navegación pero sí marca y cuenta.
  if (!negocio) {
    return (
      <Marco
        grupos={esSuperadmin ? gruposDelPanel(perfilPanel) : []}
        inicio={esSuperadmin ? '/panel' : '/'}
        titulo={esSuperadmin ? 'Panel' : undefined}
        identidadPlataforma={perfilPanel ? { nombre: perfilPanel.nombre, rol: perfilPanel.rol } : undefined}
      >
        <Routes>
          <Route path="/invitacion" element={<Invitacion />} />
          <Route path="/panel/*" element={<Permitir cuando={esSuperadmin}><Panel /></Permitir>} />
          {/* Ruta anterior conservada para enlaces guardados, igual que /login. */}
          <Route path="/plataforma/*" element={<Navigate to="/panel" replace />} />
          <Route path="/perfil" element={<Perfil conIdentidadDePlataforma={esSuperadmin === true} />} />
          <Route path="/preferencias" element={<Preferencias />} />
          {/* El único sitio donde se crea un negocio, y solo para quien no tiene ninguno. */}
          <Route
            path="/crear-negocio"
            element={
              <Permitir cuando={!fallóCarga && negociosPropios.length === 0}>
                <CrearNegocio />
              </Permitir>
            }
          />
          <Route path="*" element={<SinNegocio superadmin={esSuperadmin} sinNegocios={!fallóCarga && negociosPropios.length === 0} />} />
        </Routes>
      </Marco>
    );
  }

  const esPropietario = negocio.rol === 'propietario';
  const esContador = negocio.rol === 'contador';

  // La barra se agrupa por lo que se hace con cada entrada: lo que se opera, lo que se
  // consulta, los datos maestros que se mantienen y lo que sale hacia afuera.
  const grupos: GrupoMenu[] = (
    [
      {
        titulo: 'Operar',
        entradas: [
          { ruta: '/mostrador', texto: 'Mostrador', e2e: 'nav-mostrador', ver: !esContador },
          { ruta: '/cuadre', texto: 'Cuadre', e2e: 'nav-cuadre', ver: !esContador },
        ],
      },
      {
        titulo: 'Consultar',
        entradas: [
          { ruta: '/', texto: 'Hoy', fin: true, ver: esPropietario },
          { ruta: '/comprobantes', texto: 'Comprobantes', e2e: 'nav-comprobantes', ver: true },
          { ruta: '/gastos', texto: 'Gastos', e2e: 'nav-gastos', ver: esPropietario || esContador },
        ],
      },
      {
        titulo: 'Catálogo',
        entradas: [
          { ruta: '/catalogo', texto: 'Catálogo', e2e: 'nav-catalogo', ver: !esContador },
          { ruta: '/recetas', texto: 'Recetas', ver: esPropietario },
          { ruta: '/terceros', texto: 'Terceros', e2e: 'nav-terceros', ver: esPropietario },
        ],
      },
      {
        titulo: 'Informes',
        entradas: [
          { ruta: '/reportes', texto: 'Reportes', ver: esPropietario || esContador },
          { ruta: '/exportes', texto: 'Exportes', ver: esPropietario || esContador },
        ],
      },
      {
        titulo: 'Administración',
        // El cajero y el contador entran solo a ver el equipo; el resto de la configuración es del dueño.
        entradas: [{ ruta: '/configuracion', texto: 'Ajustes', ver: true }],
      },
    ] as { titulo: string; entradas: { ruta: string; texto: string; e2e?: string; fin?: boolean; ver: boolean }[] }[]
  )
    .map((g) => ({ titulo: g.titulo, entradas: g.entradas.filter((e) => e.ver) }))
    .filter((g) => g.entradas.length > 0);

  return (
    <Marco grupos={grupos} inicio="/">
      <Routes>
          <Route path="/" element={esPropietario ? <Hoy /> : <Navigate to={esContador ? '/reportes' : '/mostrador'} replace />} />
          <Route path="/mostrador" element={<Permitir cuando={!esContador}><Mostrador /></Permitir>} />
          <Route path="/comprobantes" element={<Comprobantes />} />
          <Route path="/comprobantes/:id" element={<DetalleComprobante />} />
          <Route path="/cuadre" element={<Permitir cuando={!esContador}><Cuadre /></Permitir>} />
          <Route path="/catalogo" element={<Permitir cuando={!esContador}><Catalogo /></Permitir>} />
          <Route path="/carta" element={<Permitir cuando={!esContador}><CartaCodigos /></Permitir>} />
          <Route path="/terceros" element={<Permitir cuando={esPropietario}><Terceros /></Permitir>} />
          <Route path="/gastos" element={<Permitir cuando={esPropietario || esContador}><Gastos /></Permitir>} />
          <Route path="/recetas" element={<Permitir cuando={esPropietario}><Recetas /></Permitir>} />
          <Route path="/reportes" element={<Permitir cuando={esPropietario || esContador}><Reportes /></Permitir>} />
          <Route path="/configuracion/:seccion?" element={<Configuracion />} />
          <Route path="/exportes" element={<Permitir cuando={esPropietario || esContador}><Exportes /></Permitir>} />
          <Route path="/invitacion" element={<Invitacion />} />
          <Route path="/perfil" element={<Perfil />} />
          <Route path="/preferencias" element={<Preferencias />} />
          <Route path="/panel/*" element={<Permitir cuando={esSuperadmin !== false}><Panel /></Permitir>} />
          <Route path="/plataforma/*" element={<Navigate to="/panel" replace />} />
          <Route path="/ingresar" element={<Navigate to="/" replace />} />
          <Route path="/login" element={<Navigate to="/" replace />} />
          <Route path="/crear-cuenta" element={<Navigate to="/" replace />} />
          <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Marco>
  );
}

/**
 * El armazón de toda página con sesión: encabezado, contenido y avisos. El encabezado se decide aquí
 * y no pantalla por pantalla, que es lo que lo hace igual en todas.
 */
function Marco({
  grupos,
  inicio,
  titulo,
  identidadPlataforma,
  children,
}: {
  grupos: GrupoMenu[];
  inicio: string;
  titulo?: string;
  identidadPlataforma?: IdentidadPlataforma;
  children: ReactNode;
}) {
  return (
    <div className="flex min-h-full flex-col">
      <Encabezado grupos={grupos} inicio={inicio} titulo={titulo} identidadPlataforma={identidadPlataforma} />
      <main className="w-full flex-1 px-4 py-4">{children}</main>
      <RetomarDestino />
      <AvisoCola />
    </div>
  );
}

/**
 * Las secciones del panel que los permisos habilitan, en el orden de la barra. La primera que el
 * perfil puede ver es también a donde entra.
 */
export const SECCIONES_PANEL = [
  { ruta: 'indicadores', texto: 'Indicadores', permiso: 'ver:indicadores', grupo: 'Panel' },
  { ruta: 'auditoria', texto: 'Auditoría', permiso: 'ver:auditoria', grupo: 'Panel' },
  { ruta: 'catalogos', texto: 'Catálogos', permiso: 'ver:catalogos', grupo: 'Panel' },
  { ruta: 'equipo', texto: 'Equipo', permiso: 'ver:analistas', grupo: 'Panel' },
  { ruta: 'negocios', texto: 'Negocios', permiso: 'ver:negocios', grupo: 'Negocios' },
  { ruta: 'operaciones', texto: 'Operaciones', permiso: 'ver:operaciones', grupo: 'Negocios' },
  { ruta: 'sesiones', texto: 'Sesiones', permiso: 'ver:sesiones', grupo: 'Negocios' },
] as const;

/** Los grupos en el orden en que aparecen en `SECCIONES_PANEL`, sin los que quedan vacíos. */
function gruposDelPanel(perfil: PerfilPlataforma | null): GrupoMenu[] {
  const visibles = SECCIONES_PANEL.filter((s) => perfil?.permisos.includes(s.permiso));
  const titulos = [...new Set(visibles.map((s) => s.grupo))];
  return titulos.map((titulo) => ({
    titulo,
    entradas: visibles
      .filter((s) => s.grupo === titulo)
      .map((s) => ({ ruta: `/panel/${s.ruta}`, texto: s.texto, e2e: `tab-${s.ruta}` })),
  }));
}

export function App() {
  useEffect(() => {
    iniciarSincronizador();
    iniciarRastro();
    // Con el tema en «sistema», seguir el cambio del sistema operativo sin recargar.
    return seguirAlSistema();
  }, []);
  return (
    <BrowserRouter>
      <ProveedorApp>
        <Enrutador />
      </ProveedorApp>
    </BrowserRouter>
  );
}
